import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Select,
} from "../ui";
import LudDashboardModal from "./LudDashboardModal";
import { useSubmitGuard } from "./modalHooks";

export default function MaintenanceClosureModal({
  open,
  lang,
  text,
  branch,
  branches = [],
  lockBranch = false,
  onClose,
  onSubmit,
}) {
  // Repeat clicks are ignored while a save returned by the caller is running.
  const [saving, runSave] = useSubmitGuard();
  const [form, setForm] = useState({
    branchId: "",
    from: "",
    to: "",
    reason: "",
  });

  useEffect(() => {
    if (!open) return;

    setForm({
      branchId: branch?.id || branches[0]?.id || "",
      from: "",
      to: "",
      reason: "",
    });
    // `branches` defaults to a new [] on every render; keying on it re-ran
    // this reset on each render.
    // Initialise when the modal opens or the entity changes, not whenever the
    // parent passes a new object/array for the same data (that wiped edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branch?.id]);

  const selectedBranch =
    branch ||
    branches.find((item) => item.id === form.branchId);

  const rangeInvalid = Boolean(form.from && form.to && new Date(form.to) < new Date(form.from));
  const branchLabel = selectedBranch ? (lang === "ar" ? selectedBranch.nameAr : selectedBranch.nameEn) : "";

  const submit = () => {
    if (!form.from || !form.to) return;
    if (new Date(form.to) < new Date(form.from)) return;

    // Returned so the save guard can wait for an async caller.
    return onSubmit?.({
      branch: selectedBranch,
      branchId: selectedBranch?.id,
      from: form.from,
      to: form.to,
      reason: form.reason.trim(),
    });
  };

  return (
    <LudDashboardModal
      open={open}
      busy={saving}
      title={
        lang === "ar"
          ? "طلب إغلاق صيانة"
          : "Maintenance Closure Request"
      }
      onClose={onClose}
      footer={
        <>
          <Button
            variant="primary"
            appearance="filled"
            disabled={
              saving ||
              !form.from ||
              !form.to ||
              new Date(form.to) < new Date(form.from)
            }
            loading={saving}
            onClick={() => runSave(submit)}
          >
            {lang === "ar"
              ? "إرسال الطلب"
              : "Send Request"}
          </Button>

          <Button
            variant="danger"
            appearance="soft"
            onClick={onClose}
          >
            {text.cancel}
          </Button>
        </>
      }
    >
      <div className="lud-request-modal-form">
        <p className="lud-request-modal-hint">
          {lang === "ar"
            ? "حدد فترة الإغلاق المتوقعة بسبب الصيانة."
            : "Select the expected closure period for maintenance."}
        </p>

        {lockBranch && branchLabel && (
          <p className="lud-request-modal-hint">
            <strong>{lang === "ar" ? "الفرع المتأثر: " : "Affected branch: "}</strong>
            {branchLabel}
          </p>
        )}

        {!lockBranch && (
          <Select
            label={lang === "ar" ? "الفرع" : "Branch"}
            value={form.branchId}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                branchId: event.target.value,
              }))
            }
            options={branches.map((item) => ({
              value: item.id,
              label:
                lang === "ar"
                  ? item.nameAr
                  : item.nameEn,
            }))}
          />
        )}

        <div className="lud-request-period">
          <Input
            type="date"
            label={lang === "ar" ? "من" : "From"}
            value={form.from}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                from: event.target.value,
              }))
            }
          />

          <Input
            type="date"
            label={lang === "ar" ? "إلى" : "To"}
            value={form.to}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                to: event.target.value,
              }))
            }
          />
        </div>

        {rangeInvalid && (
          <p className="lud-hours-error" role="alert">
            {lang === "ar"
              ? "تاريخ نهاية الإغلاق يجب أن يكون في نفس يوم البداية أو بعده."
              : "The closure end date must be on or after the start date."}
          </p>
        )}

        <Input
          textarea
          maxLength={500}
          label={lang === "ar" ? "السبب والملاحظات (اختياري)" : "Reason and notes (optional)"}
          value={form.reason}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              reason: event.target.value,
            }))
          }
        />

        {form.from && form.to && !rangeInvalid && (
          <section className="lud-hours-summary" aria-label={lang === "ar" ? "ملخص الإغلاق" : "Closure summary"}>
            <h4>{lang === "ar" ? "ملخص الإغلاق المطلوب" : "Requested closure"}</h4>
            <p dir="auto">
              {branchLabel ? `${branchLabel}: ` : ""}
              <span dir="ltr">{form.from}</span> → <span dir="ltr">{form.to}</span>
            </p>
            <p className="lud-hours-hint">
              {lang === "ar"
                ? "لن يُغلق الفرع الآن: يراجع المطعم أو إدارة LUD الطلب ثم تؤكد التطبيقات."
                : "The branch is not closed now: the restaurant or LUD reviews the request, then the applications confirm."}
            </p>
          </section>
        )}
      </div>
    </LudDashboardModal>
  );
}
